import { ArrowClockwiseIcon } from "@phosphor-icons/react";
import { useEffect, useMemo, useState } from "react";

import { apiRequest } from "../api/client";
import { Button } from "../components/Button";
import { EmptyState } from "../components/EmptyState";
import { LoadingState } from "../components/LoadingState";
import type { AuditLog } from "../types";

export function ActivityPage() {
  const [items, setItems] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [resourceType, setResourceType] = useState("");

  const resourceTypes = useMemo(
    () => [...new Set(items.map((item) => item.resource_type))].sort(),
    [items],
  );
  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return items.filter((item) => {
      if (resourceType && item.resource_type !== resourceType) return false;
      if (!normalized) return true;
      return [
        item.action,
        item.resource_type,
        item.resource_id,
        item.request_id,
        JSON.stringify(item.payload_json),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(normalized);
    });
  }, [items, query, resourceType]);

  async function load() {
    setLoading(true);
    setError("");
    try {
      setItems(await apiRequest<AuditLog[]>("/api/v1/admin/activity"));
    } catch {
      setError("Не удалось загрузить журнал действий");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <span className="eyebrow">Контроль</span>
          <h1>Журнал действий</h1>
          <p>
            История изменений страниц, локалей, публикаций, команды и
            технических операций Command Center.
          </p>
        </div>
        <Button onClick={() => void load()} tone="secondary">
          <ArrowClockwiseIcon size={16} /> Обновить
        </Button>
      </header>

      {error ? <div className="notice notice--error">{error}</div> : null}
      {loading ? (
        <LoadingState rows={8} />
      ) : items.length ? (
        <section className="event-log activity-log">
          <div className="table-toolbar">
            <label>
              <span>Поиск</span>
              <input
                onChange={(event) => setQuery(event.target.value)}
                placeholder="page.published, id, request"
                value={query}
              />
            </label>
            <label>
              <span>Ресурс</span>
              <select
                onChange={(event) => setResourceType(event.target.value)}
                value={resourceType}
              >
                <option value="">Все ресурсы</option>
                {resourceTypes.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </label>
            <strong>{filtered.length} записей</strong>
          </div>
          <div className="event-log__head event-log__head--activity">
            <span>Действие</span>
            <span>Ресурс</span>
            <span>Payload</span>
            <span>Время</span>
          </div>
          {filtered.map((item) => (
            <article
              className="event-log__row event-log__row--activity"
              key={item.id}
            >
              <strong>{item.action}</strong>
              <span>
                {item.resource_type}
                {item.resource_id ? ` · ${item.resource_id}` : ""}
              </span>
              <code>{compactPayload(item.payload_json)}</code>
              <span>{new Date(item.created_at).toLocaleString()}</span>
            </article>
          ))}
        </section>
      ) : (
        <EmptyState
          title="Журнал пока пуст"
          description="Сохраните страницу или измените настройки — запись появится здесь."
        />
      )}
    </div>
  );
}

function compactPayload(value: Record<string, unknown>) {
  const text = JSON.stringify(value);
  return text.length > 160 ? `${text.slice(0, 157)}…` : text;
}
